import React, { useState, useEffect } from 'react';
import { Navbar, Container } from 'react-bootstrap';
import { useNavigate, useLocation } from 'react-router-dom';
import { styles } from './NavbarStyles';
import NavbarItem from './NavbarItem';
import { useTheme } from '../../context/ThemeContext';
import { useCompany } from '../../context/CompanyContext';
import { useAuth } from '../../context/AuthContext';
import CustomDropdown from '../ui/dropdowns/CustomDropdown';
import DevelopmentBanner from '../ui/feedback/DevelopmentBanner';
import HeaderClock from './HeaderClock';
import NavbarExportMenu from './NavbarExportMenu';

export const APP_NAME = 'JC&Retenciones';

// Mapa de rutas a títulos
const ROUTE_TITLES = {
  '/': 'Panel de Control',
  '/dashboard': 'Panel de Control',
  '/proveedores': 'Proveedores',
  '/empleados': 'Empleados',
  '/retenciones/islr': 'Retenciones ISLR',
  '/retenciones/iva': 'Retenciones I.V.A.',
  '/retenciones/otros': 'Otros Impuestos',
  '/configuracion': 'Configuración'
};

/**
 * ------------------------------------------------------------------
 * Navbars
 * Componente principal de la barra de navegación superior.
 * Maneja el logo de la empresa, el menú principal y las notificaciones/perfil.
 * ------------------------------------------------------------------
 * INDICE DE FUNCIONES
 * 1. getCompanyInitials ............. Obtiene las iniciales del nombre de la empresa
 * 2. Navbars ........................ Componente principal
 * ------------------------------------------------------------------
 * PROPS
 * @param {function} onMenuClick - Callback para abrir el menú lateral (drawer) en móvil
 * ------------------------------------------------------------------
 */

function Navbars({ onMenuClick, onExportISLR, onExportIVA }) {
  // ------------------------------------------------------------------
  // Hooks & State
  // ------------------------------------------------------------------
  const { dark } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { company } = useCompany();
  const [companyLogo, setCompanyLogo] = useState(null);

  // Obtener el título de la página actual
  const getCurrentTitle = () => {
    // Exact match
    if (ROUTE_TITLES[location.pathname]) {
      return ROUTE_TITLES[location.pathname];
    }
    // Partial Match (e.g. /retenciones/islr/crear)
    const key = Object.keys(ROUTE_TITLES).find(k => k !== '/' && location.pathname.startsWith(k));
    return key ? ROUTE_TITLES[key] : APP_NAME;
  };

  const pageTitle = getCurrentTitle();

  // ------------------------------------------------------------------
  // 1. getCompanyInitials
  // ------------------------------------------------------------------
  const getCompanyInitials = (companyName) => {
    if (!companyName) return '';
    const words = companyName.trim().split(/\s+/);
    if (words.length >= 2) {
      return (words[0][0] + words[1][0]).toUpperCase();
    }
    return companyName.substring(0, 2).toUpperCase();
  };

  // ------------------------------------------------------------------
  // Effects
  // ------------------------------------------------------------------
  // Actualizar logo cuando cambie la empresa
  useEffect(() => {
    if (company?.ruta_logo) {
      // Add cache buster to force reload when logo changes
      const timestamp = new Date().getTime();
      setCompanyLogo(`${company.ruta_logo}?t=${timestamp}`);
    } else {
      setCompanyLogo(null);
    }
  }, [company]);

  const { logout } = useAuth();

  const rightDropdownItems = [
    { label: 'Configuración', icon: 'bi-gear', onClick: () => navigate('/configuracion') },
    { divider: true },
    { label: 'Cerrar sesión', icon: 'bi-box-arrow-right', onClick: logout }
  ];

  // ------------------------------------------------------------------
  // 2. Render Principal
  // ------------------------------------------------------------------
  return (
    <div className="d-flex flex-column w-100 fixed-top">
      <Navbar
        bg={dark ? "dark" : "white"}
        variant={dark ? "dark" : "light"}
        expand="lg"
        className={`shadow-sm ${dark ? 'navbar-dark bg-dark' : ''}`}
        style={{ ...styles.navbarFixed, position: 'relative' }} // Override fixed
      >
        <Container fluid className="app-main-container flex-nowrap align-items-center">
          {/* MOBILE: Page Title (Left) */}
          <div className="d-lg-none d-flex align-items-center me-auto">
            <img
              src={`${process.env.PUBLIC_URL}/logo.png`}
              alt="Logo"
              className="me-2"
              style={{ height: '28px', width: 'auto' }}
              onError={(e) => { e.target.style.display = 'none'; }}
            />
            <span className="fw-bold fs-5 text-truncate text-primary" style={{ maxWidth: '200px' }}>
              {pageTitle}
            </span>
          </div>

          {/* DESKTOP: Brand/Logo (Left) */}
          <Navbar.Brand className={`${styles.classNavbarBrand} d-none d-lg-flex flex-column align-items-start`} href="/">
            <div className="d-flex align-items-center">
              <img
                src={`${process.env.PUBLIC_URL}/logo.png`}
                alt="Logo"
                className="navbar-logo"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
              <div className="d-flex flex-column">
                <span>{APP_NAME}</span>
                <small className="text-muted" style={{ fontSize: '11px', marginTop: '-4px' }}>
                  Sistema de Gestión de Retenciones Fiscales
                </small>
              </div>
            </div>

          </Navbar.Brand>

          {/* DESKTOP: Clock (Center) */}
          <div className="d-none d-lg-flex justify-content-center flex-grow-1">
            <HeaderClock />
          </div>

          {/* DESKTOP: Links (Right) */}
          <div className="d-none d-lg-flex align-items-center">
            <NavbarItem />
            <div className="border-start mx-2" style={{ height: '24px', opacity: 0.3 }}></div>
            <NavbarExportMenu onExportISLR={onExportISLR} onExportIVA={onExportIVA} />
          </div>

          {/* DESKTOP: Profile (Right) - Hidden on Mobile */}
          <div className={`${styles.classDivNotification} d-none d-lg-flex ms-3`}>
            {/* Notification Icon removed as per request */}
            <CustomDropdown
              label=""
              avatar={companyLogo}
              initials={!companyLogo ? getCompanyInitials(company?.razon_social) : null}
              items={rightDropdownItems}
            />
          </div>

          {/* MOBILE: Hamburger Toggle (Right) */}
          <Navbar.Toggle
            aria-controls="basic-navbar-nav"
            className="d-lg-none ms-2 border-0"
            onClick={e => {
              e.preventDefault();
              onMenuClick();
            }}
          />
        </Container>
      </Navbar>
      <DevelopmentBanner />
    </div>
  );
}

export default Navbars;